import React, { useState } from 'react';
import { Button, Card, Typography, Space, Alert, Spin } from 'antd';
import { ApiOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import mongoRealService from '../../services/mongoRealService';

const { Title, Text } = Typography;

const ApiTest = () => {
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState({});

  const testConnection = async () => {
    setTesting(true);
    const testResults = {};

    try {
      // Test 1: Fetch Employees
      try {
        const employees = await mongoRealService.getEmployees();
        testResults.employees = {
          success: true,
          data: employees,
          message: `Found ${employees.length} employees`
        };
      } catch (error) {
        testResults.employees = {
          success: false,
          error: error.message
        };
      }

      // Test 2: Fetch Projects
      try {
        const projects = await mongoRealService.getProjects();
        testResults.projects = {
          success: true,
          data: projects,
          message: `Found ${projects.length} projects`
        };
      } catch (error) {
        testResults.projects = {
          success: false,
          error: error.message
        };
      }

      // Test 3: Fetch Timesheets
      try {
        const timesheets = await mongoRealService.getAllTimesheets();
        testResults.timesheets = {
          success: true,
          data: timesheets,
          message: `Found ${timesheets.length} timesheet entries`
        };
      } catch (error) {
        testResults.timesheets = {
          success: false,
          error: error.message
        };
      }

    } catch (error) {
      testResults.general = {
        success: false,
        error: error.message
      };
    }

    setResults(testResults);
    setTesting(false);
  };

  const TestResult = ({ title, result }) => {
    if (!result) return null;

    return (
      <div className="mb-4">
        <div className="flex items-center mb-2">
          {result.success ? (
            <CheckCircleOutlined className="text-green-500 mr-2" />
          ) : (
            <CloseCircleOutlined className="text-red-500 mr-2" />
          )}
          <Text strong>{title}</Text>
        </div>
        {result.success ? (
          <Alert
            message={result.message}
            type="success"
            showIcon={false}
            className="mb-2"
          />
        ) : (
          <Alert
            message="Connection Failed"
            description={result.error}
            type="error"
            showIcon={false}
            className="mb-2"
          />
        )}
        {result.data && (
          <details className="mt-2">
            <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
              View Data ({result.data.length} items)
            </summary>
            <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Card className="shadow-lg">
        <div className="text-center mb-6">
          <Title level={3}>
            <ApiOutlined className="mr-2" />
            MongoDB Atlas API Connection Test
          </Title>
          <Text className="text-gray-600">
            Test the connection to MongoDB Atlas and verify data structure
          </Text>
        </div>

        <div className="mb-6">
          <Alert
            message="Configuration"
            description={
              <div>
                <p><strong>MongoDB Atlas:</strong> ✓ Configured</p>
                <p><strong>Database:</strong> {process.env.REACT_APP_DATABASE_NAME || 'timesheet_db'}</p>
              </div>
            }
            type="success"
            showIcon
            className="mb-4"
          />
        </div>

        <div className="text-center mb-6">
          <Button
            type="primary"
            size="large"
            icon={<ApiOutlined />}
            onClick={testConnection}
            loading={testing}
          >
            {testing ? 'Testing Connection...' : 'Test API Connection'}
          </Button>
        </div>

        {testing && (
          <div className="text-center mb-6">
            <Spin size="large" />
            <p className="mt-2 text-gray-600">Testing MongoDB Atlas connection...</p>
          </div>
        )}

        {Object.keys(results).length > 0 && (
          <div>
            <Title level={4}>Test Results</Title>
            <TestResult title="Employees Collection" result={results.employees} />
            <TestResult title="Projects Collection" result={results.projects} />
            <TestResult title="Timesheets Collection" result={results.timesheets} />
            {results.general && <TestResult title="General Error" result={results.general} />}
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <Title level={5}>MongoDB Collections Structure:</Title>
          <Space direction="vertical" className="w-full">
            <div>
              <Text strong>Employees Collection:</Text>
              <Text className="block text-sm text-gray-600">Fields: name, email, password, department, position, isActive</Text>
            </div>
            <div>
              <Text strong>Projects Collection:</Text>
              <Text className="block text-sm text-gray-600">Fields: name, description, client, status, isActive</Text>
            </div>
            <div>
              <Text strong>Timesheets Collection:</Text>
              <Text className="block text-sm text-gray-600">Fields: employeeId, employeeName, employeeEmail, projectId, projectName, date, hours, description, status</Text>
            </div>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default ApiTest;
